import { useMemo, useState, type ReactElement } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { can } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import { AdminConfigsTable } from '@/features/admin-configs/components/AdminConfigsTable';
import { ConfigDetailSheet } from '@/features/admin-configs/components/ConfigDetailSheet';
import {
  ConfigToolbar,
  type ConfigToolbarOptions,
} from '@/features/admin-configs/components/ConfigToolbar';
import type { ConfigItemViewModel } from '@/features/admin-configs/api';
import {
  useGlobalConfigCatalog,
  useGlobalConfigList,
  useGlobalConfigMutations,
  type GlobalConfigFilters,
} from '@/features/platform-configs/api';

const EMPTY_FILTERS: GlobalConfigFilters = {};

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

/**
 * Platform-console "Config (Global)" page (PTV2-1383): the deployment-wide
 * counterpart of {@link AdminConfigsPage}, backed by `/api/v1/global-configs`.
 * Reuses the admin-configs presentational components verbatim; scope is
 * implicitly GLOBAL (no scope facet). Gated by `managePlatform` upstream in the
 * route table.
 */
export function PlatformConfigsPage(): ReactElement {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<GlobalConfigFilters>(EMPTY_FILTERS);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const auth = useAuth();
  const canManage = can(auth.user, 'managePlatform');

  const catalogQuery = useGlobalConfigCatalog();
  const listQuery = useGlobalConfigList(filters);
  const { upsert, reset } = useGlobalConfigMutations();

  // Scope is fixed to GLOBAL, so the scope facet is intentionally left empty.
  const toolbarOptions: ConfigToolbarOptions = useMemo(() => {
    const defs = catalogQuery.data ?? [];
    return {
      namespaces: uniqueSorted(defs.map((d) => d.namespace)),
      tags: uniqueSorted(defs.flatMap((d) => d.tags)),
      scopes: [],
    };
  }, [catalogQuery.data]);

  const configs = useMemo(
    () => (listQuery.isSuccess ? [...listQuery.data].sort((a, b) => a.key.localeCompare(b.key)) : []),
    [listQuery.isSuccess, listQuery.data],
  );

  const selected: ConfigItemViewModel | null = useMemo(
    () => configs.find((c) => c.key === selectedKey) ?? null,
    [configs, selectedKey],
  );

  const selectedEnumValues = useMemo(() => {
    if (selectedKey === null) return null;
    return catalogQuery.data?.find((d) => d.key === selectedKey)?.enumValues ?? null;
  }, [catalogQuery.data, selectedKey]);

  function handleFilterChange(next: Partial<GlobalConfigFilters>): void {
    setFilters((prev) => ({ ...prev, ...next }));
  }

  function handleCloseSheet(): void {
    if (upsert.isPending || reset.isPending) return;
    setSelectedKey(null);
    upsert.reset();
    reset.reset();
  }

  function handleSave(input: { value: string; reason?: string }): void {
    if (selectedKey === null) return;
    upsert.mutate(
      {
        key: selectedKey,
        value: input.value,
        ...(input.reason !== undefined ? { reason: input.reason } : {}),
      },
      {
        onSuccess: () => {
          upsert.reset();
        },
      },
    );
  }

  function handleReset(): void {
    if (selectedKey === null) return;
    reset.mutate(selectedKey, {
      onSuccess: () => {
        reset.reset();
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<SlidersHorizontal className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.platform.configs.heading')}
        subtitle={t('routes.platform.configs.subheading')}
      />
      <div className="space-y-4 bg-background px-8 py-5">
        <ConfigToolbar filters={filters} options={toolbarOptions} onChange={handleFilterChange} />

        {listQuery.isPending && <ConfigsListSkeleton />}

        {listQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={listQuery.error}
              heading={t('routes.admin.configs.error.heading')}
              message={t('routes.admin.configs.error.message')}
              codeLabel={t('routes.admin.configs.error.codeLabel')}
              onRetry={() => {
                listQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.admin.configs.error.retry')}
            />
          </div>
        )}

        {listQuery.isSuccess && configs.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t('routes.platform.configs.empty')}
          </p>
        )}

        {listQuery.isSuccess && configs.length > 0 && (
          <AdminConfigsTable
            configs={configs}
            selectedKey={selectedKey}
            onSelect={(config) => {
              upsert.reset();
              reset.reset();
              setSelectedKey(config.key);
            }}
          />
        )}
      </div>

      <ConfigDetailSheet
        config={selected}
        enumValues={selectedEnumValues}
        canEditSensitive={canManage}
        onClose={handleCloseSheet}
        onSave={handleSave}
        onReset={handleReset}
        savePending={upsert.isPending}
        resetPending={reset.isPending}
        saveError={upsert.error}
        resetError={reset.error}
      />
    </>
  );
}

function ConfigsListSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
